import {
  Button,
  Combobox,
  DatePicker,
  Flex,
  Heading,
  Select,
  TextInput,
  TextInputProps,
  createDatePickerValidator,
  createForm,
  useToasts,
} from '@applyboard/crystal-ui'
import { COUNTRY_NAMES } from '@applyboard/school-backend-lib/dist/common-types/country'
import { AccountCircleOutlineIcon } from '@applyboard/ui-icons'
import { GENDER_NAMES } from '@applyboard/school-backend-lib/dist/common-types/gender'
import { Loading } from '../../components/Loading'
import { NavBars } from '../../components/NavBars'
import { useGetProfile, useLoggedInUser, useUpdateProfile } from '../../hooks'
import { convertTimelessDateStrToLocalDate } from '../../utils/convertTimelessDateStrToLocalDate'
import { ComponentType } from 'react'

type ProfileFormFields = {
  givenName: string
  familyName: string
  middleName: string
  dateOfBirth: string
  nationality: string
  gender: string
  email: string
}

const { Form, Field } = createForm<ProfileFormFields>()

export function ProfilePage() {
  const toast = useToasts()
  const { username } = useLoggedInUser()
  const { isLoadingProfile, profile } = useGetProfile({ id: username })
  const { isUpdatingProfile, updateProfile } = useUpdateProfile({ id: username })

  if (isLoadingProfile) {
    return (
      <NavBars>
        <Loading />
      </NavBars>
    )
  }

  if (!profile) {
    return null
  }

  return (
    <NavBars>
      <Form
        defaultValues={{
          givenName: profile.attributes.givenName,
          familyName: profile.attributes.familyName || '',
          middleName: profile.attributes.middleName || '',
          dateOfBirth: profile.attributes.dateOfBirth
            ? convertTimelessDateStrToLocalDate(profile.attributes.dateOfBirth).toISOString()
            : '',
          nationality: profile.attributes.nationality || '',
          gender: profile.attributes.gender || '',
          email: profile.attributes.email || '',
        }}
        onSubmit={data => {
          updateProfile(data, {
            onSuccess: () => {
              toast.positive('Changes to your profile successfully saved.')
            },
            onError: err => {
              if (err instanceof Error) {
                toast.negative(err)
              } else {
                toast.negative(new Error('Something went wrong when saving your profile.'))
              }
            },
          })
        }}
      >
        <ProfilePageForm loading={isUpdatingProfile} />
      </Form>
    </NavBars>
  )
}

type ProfilePageFormProps = {
  loading: boolean
}

function ProfilePageForm(props: ProfilePageFormProps) {
  const maxDate = new Date()
  const minDate = new Date(1900, 0, 1)

  const datePickerValidator = createDatePickerValidator(minDate, maxDate, 'day')

  return (
    <>
      <Flex align="center" justify="between" pb={4}>
        <Flex gap={4} align="center">
          <AccountCircleOutlineIcon contrast="mid" intent="primary" size="xl" />
          <Heading level={1} variant="headlineL">
            My Profile
          </Heading>
        </Flex>
        <Flex hideBelow="sm">
          <Button intent="primary" type="submit" loading={props.loading}>
            Save
          </Button>
        </Flex>
      </Flex>
      <Flex gap={6} pb={{ xs: 6, sm: 10 }} direction={{ xs: 'column', sm: 'row' }}>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(33% - 12px)' }}>
          <Field
            as={TextInput as ComponentType<TextInputProps>}
            label="Given Name"
            name="givenName"
            validate={givenName => {
              if (!givenName) {
                return 'This field is required.'
              }

              return true
            }}
          />
        </Flex.Item>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(33% - 12px)' }}>
          <Field
            as={TextInput as ComponentType<TextInputProps>}
            label="Family Name"
            name="familyName"
          />
        </Flex.Item>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(33% - 12px)' }}>
          <Field
            as={TextInput as ComponentType<TextInputProps>}
            label="Middle Name (optional)"
            name="middleName"
          />
        </Flex.Item>
      </Flex>
      <Flex gap={6} pb={{ xs: 6, sm: 10 }} direction={{ xs: 'column', sm: 'row' }}>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(33% - 12px)' }}>
          <Field
            as={DatePicker}
            label="Date of Birth"
            name="dateOfBirth"
            maxDate={maxDate.toISOString()}
            minDate={minDate.toISOString()}
            validate={datePickerValidator}
          />
        </Flex.Item>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(33% - 12px)' }}>
          <Field
            as={Combobox}
            label="Nationality"
            name="nationality"
            size="md"
            placeholder="Select"
          >
            {Object.entries(COUNTRY_NAMES).map(([countryCode, countryName]) => (
              <Combobox.Option key={countryCode} label={countryName} value={countryCode} />
            ))}
          </Field>
        </Flex.Item>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(33% - 12px)' }}>
          <Field as={Select} label="Gender" name="gender" appearance="styled">
            {Object.entries(GENDER_NAMES).map(([genderCode, genderName]) => (
              <Select.Option key={genderCode} label={genderName} value={genderCode} />
            ))}
          </Field>
        </Flex.Item>
      </Flex>
      <Flex gap={6} pb={{ xs: 6, sm: 10 }} direction={{ xs: 'column', sm: 'row' }}>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(33% - 12px)' }}>
          <Field
            as={TextInput as ComponentType<TextInputProps>}
            label="Email"
            name="email"
            disabled
          />
        </Flex.Item>
      </Flex>
      <Flex hideAbove="sm">
        <Button intent="primary" type="submit" width="fill" loading={props.loading}>
          Save
        </Button>
      </Flex>
    </>
  )
}
